import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, Shield, Trash2, AlertTriangle, Loader2, UserCog, Check, X, UserPlus, Copy, Mail, FileText, Pencil, QrCode, Database, Archive, LogOut, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { lifecycleEndpoint } from "../lib/lifecycleEndpoint";
import ProjectHeader from "../components/ProjectHeader";
import ProjectInviteQRDialog from "../components/ProjectInviteQRDialog";
import ScriptSection from "../components/ScriptSection";
import EpisodicScriptSetup from "../components/EpisodicScriptSetup";
import ThemeToggle from "../components/ThemeToggle";
import ExportDialog, { RecentExportsList } from "../components/ExportDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";
import ConfirmDialog from "../components/ConfirmDialog";
import TransferOwnershipDialog from "../components/TransferOwnershipDialog";
import { ArchiveIntegrityCard } from "../components/ArchiveIntegrity";
import ScriptsCollapsibleGroup from "../components/ScriptsCollapsibleGroup";
import { useRefreshOnPermissionStale } from "../hooks/useProjectRoleRefresh";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

// Iter-156A.4 — canonical role labels and matching one-sentence
// "what this role can do" copy used by the FE everywhere a member's
// role is shown. Kept in sync with the backend role tokens in
// /backend/server.py (ROLE_OWNER / ROLE_ADMIN / ROLE_CONTRIBUTOR /
// ROLE_VIEWER). Plain HOD-friendly language — no developer jargon.
//
// Iter-158.13.2.14 — Team & Access on the Production Overview page
// governs PRODUCTION privileges (production-scope authority). The
// labels below are prefixed accordingly so users can't confuse
// them with the DEPARTMENT privilege shown on Front Door department
// cards ("Hair & Makeup · Contributor" etc.). Department privileges
// live in each department's own workspace and are independent.
// Iter-158.13.2.20f.1 (#16) — vocabulary consolidation.
// Retire "Administrator" in favour of "Admin" so ProjectSettings
// labels match `workspaceDestination.PRODUCTION_ROLE_LABELS` used
// by My Projects. Same user must never see two different labels
// (e.g. "Production Viewer" on My Projects but "Production
// Contributor" on Settings) for the same effective role.
const ROLE_LABELS = {
    owner: "Production Owner",
    admin: "Production Admin",
    contributor: "Production Contributor",
    viewer: "Production Viewer",
};
const ROLE_DESCRIPTIONS = {
    owner: "Full control of the production — invite, manage, archive, delete, transfer ownership. Production Owner also has oversight access to every department.",
    admin: "Governs the production — invite users, change roles for Contributors & Viewers, edit production-level settings. Does NOT automatically govern any specific department; department privileges are managed inside each department.",
    contributor: "Production-level participation — see production-wide screens and canonical script. Cannot manage users or governance. Department-level work depends on department privileges.",
    viewer: "Read-only at production level — can browse production-wide screens. Cannot make production-level changes. Department work depends on department privileges.",
};

// Mirror the canonical keys used at create-time so admins see the
// human-readable label rather than the raw key.
const PRODUCTION_TYPE_LABELS = {
    feature_film: "Feature Film",
    tv_single: "Single TV Episode",
    tv_series: "Episodic TV Series",
};

// Iter-157F — Settings IA refresh. Small calm group header strip
// placed BEFORE each thematic block (Project Overview → Scripts →
// Team & Access → Recovery & Protection → Ownership & Lifecycle →
// Legal & Governance). Pure visual scaffolding — no controls, no
// state — so the page reads as a progression from "what is this
// project" through "increasingly consequential actions". The
// numbered prefix is a soft anchor; the subtitle is the user's own
// mental model of the question the group answers.
function SectionGroupHeader({ index, title, question, testid }) {
    // Iter-158.13.2.20f.5 #7 — numeric prefix retired from visible
    // section labels. `index` is preserved on the prop signature so
    // every existing call-site continues to compile, but the number
    // is no longer rendered. Underlying Production Management
    // architecture (ordering, groups) is untouched.
    void index;
    return (
        <header
            data-testid={testid}
            className="mt-2 mb-3 pl-1"
        >
            <div className="flex items-baseline gap-3">
                <span className="text-[10px] uppercase tracking-[0.28em] text-neutral-500 font-semibold">
                    {title}
                </span>
            </div>
            {question ? (
                <p className="text-[11px] text-neutral-500 mt-1 italic">
                    {question}
                </p>
            ) : null}
        </header>
    );
}

// Iter-158.13.2.20e.2 — The Production Hub GET returns
// `{project, script}` while the H&M mount returns a flat project
// doc. This helper normalises both shapes to the flat DTO
// ProjectSettings assumes.
function _unwrapProject(data) {
    if (data && typeof data === "object" && data.project && data.project.id) {
        return data.project;
    }
    return data;
}

export default function ProjectSettings() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();

    // Iter-157O.3 — Anchor jumps inside the Project Protection card
    // previously used scrollIntoView({block:"start"}), which slipped
    // the target's title BEHIND the sticky ProjectHeader (h-14,
    // 56px). The user perceived this as "the buttons don't jump to
    // the right place". Worse, `window.scrollTo` was a no-op here
    // because the actual scroll container is `#root` (index.css
    // pins html/body/#root to height:100% with overflow-x:hidden,
    // which promotes #root to the scrolling element). This helper
    // finds the nearest scrollable ancestor and scrolls it by the
    // element's offset, minus the sticky header height + breathing
    // room, so the destination card's heading lands just below the
    // header, every time.
    const scrollToSection = (testid) => {
        const el = document.querySelector(`[data-testid="${testid}"]`);
        if (!el) return;
        const HEADER_OFFSET = 72; // sticky header (56px) + 16px breathing room
        // Find the nearest ancestor that is actually a scroll container.
        let scroller = el.parentElement;
        while (scroller && scroller !== document.body) {
            const cs = window.getComputedStyle(scroller);
            if (
                scroller.scrollHeight > scroller.clientHeight &&
                /(auto|scroll)/.test(cs.overflowY)
            ) {
                break;
            }
            scroller = scroller.parentElement;
        }
        if (!scroller || scroller === document.body) {
            scroller = document.scrollingElement || document.documentElement;
        }
        const elRect = el.getBoundingClientRect();
        const scRect = scroller.getBoundingClientRect();
        const top = scroller.scrollTop + (elRect.top - scRect.top) - HEADER_OFFSET;
        scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const [project, setProject] = useState(null);
    const [error, setError] = useState("");

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    // Iter-135 — Archive dialog state (separate from Delete to keep
    // the operational lifecycle distinct from the destructive zone).
    const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
    // Iter-157 — Export dialog + recent-exports refresh key.
    const [exportOpen, setExportOpen] = useState(false);
    const [exportRefreshKey, setExportRefreshKey] = useState(0);
    const [archiving, setArchiving] = useState(false);

    // Owner-only state. Members + pending permission requests are loaded
    // lazily once we confirm the user is the project owner; non-owners
    // never see these sections, so we don't waste their bandwidth either.
    const [members, setMembers] = useState([]);
    const [permLoading, setPermLoading] = useState(false);
    const [permPendingMember, setPermPendingMember] = useState(null);

    // Iter-156A.4 Phase 4 — governance UI state.
    // Pending role change (held while the user confirms).
    const [pendingRoleChange, setPendingRoleChange] = useState(null);
    // Remove member confirmation.
    const [pendingRemove, setPendingRemove] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    // Transfer ownership dialog.
    const [transferOpen, setTransferOpen] = useState(false);
    const [transferBusy, setTransferBusy] = useState(false);

    // Invite member dialog state
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteName, setInviteName] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [inviteError, setInviteError] = useState("");
    const [invitePreview, setInvitePreview] = useState(null);
    const [inviteCopied, setInviteCopied] = useState(false);

    // Iter-96 PART 6 — owner-side inline edit of a member's display name.
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [editMemberName, setEditMemberName] = useState("");
    const [editMemberSaving, setEditMemberSaving] = useState(false);

    // Iter-116 — invite tools migrated from ProjectHome (which is now
    // strictly a working dashboard). QR + copy-link + visible URL.
    // Iter-158.12.6 UAT fix — when this page mounts inside the beta
    // workspace (apiBase === "/beta/hm-app/projects") the invite URL
    // MUST target the beta join path `/join/beta/{code}` so
    // JoinBetaProject.jsx dispatches to /api/beta/projects/join. The
    // legacy `/join/{code}` route calls /api/projects/join which
    // returns "Invalid invite code" because beta codes live in
    // `beta_projects`. Both Copy Link and the QR dialog derive from
    // `inviteLink` so this single guard fixes both surfaces.
    const [qrOpen, setQrOpen] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const isBetaWorkspace = apiBase.startsWith("/beta/");
    // Iter-158.12.12 — department-aware invitations. When Settings
    // is mounted inside the H&M workspace (either at
    // /beta/hm-app/projects/:id/settings or the equivalent
    // department-scoped path), invites minted here carry
    // `?dept=hm` so JoinBetaProject.jsx lands the invitee directly
    // in the H&M workspace instead of the production dept picker.
    // A generic project-level Settings page (not implemented yet
    // for beta) would pass no dept and keep the picker landing.
    // Extensible: future departments (art/costume/vfx) mint with
    // their own dept token via the same mechanism.
    // Iter-158.13.2.20e.2 — Production Team & Access is dept-agnostic.
    // Only the H&M workspace mount (apiBase === "/beta/hm-app/projects")
    // stamps invitations with `?dept=hm`. The Production mount
    // (apiBase === "/beta/projects") mints Production-scope invites
    // with no department context.
    const inviteDepartment = apiBase === "/beta/hm-app/projects" ? "hm" : null;
    // Iter-158.13.2.4 — shareable dept invite. Copy Link + QR
    // previously derived their URL from the project-level
    // `invite_code` + a client-side `?dept=hm` — which the
    // backend had no per-invitation record to back, so invitees
    // were added to project.members[] but NOT to
    // beta_departments.hair_makeup.members[]. The resulting
    // /beta/hm-app/... landing then 404'd (project_not_found).
    // Now Copy Link + QR both use the URL from the REUSABLE
    // dept-scoped shareable invitation minted by the backend
    // (capped at contributor per governance rule a.i). Email
    // invites remain per-recipient and may carry the selected
    // role. Rotation invalidates the previous shareable code.
    // Iter-158.13.2.7 — One invitation object per department surface.
    // Removes the silent legacy fallback that previously downgraded
    // Copy Link / QR to a project-scoped `invite_code + ?dept=hm`
    // URL when the shareable mint call failed. Missing/broken
    // shareable is now a visible error state, not an unsafe
    // fallback. Pill, Copy Link URL, QR image and QR caption all
    // consume `shareable` alone in dept context.
    const [shareable, setShareable] = useState(null);
    const [shareableLoading, setShareableLoading] = useState(false);
    const [shareableError, setShareableError] = useState("");
    const [shareableRotating, setShareableRotating] = useState(false);
    // Iter-158.13.2.20e.8 — Department-shareable state lives in
    // `beta_shareable_invites` and is served by BOTH mounts. Before
    // .20e.8 the H&M mount's broad `.20e.2` neutralization
    // (`can_admin=(lambda _p: False)`) denied even the Production
    // Owner. That broad gate is now overridden ONLY for the two
    // shareable-invite endpoints on the H&M mount (see
    // `can_admin_department_invite` on the H&M team_access
    // registration in server.py). The FE therefore consumes the
    // endpoint from whichever mount is active — no client-side URL
    // swap required. Kept the variable for clarity so future audits
    // can see the scope alignment at a glance.
    const shareableApiBase = apiBase;
    useEffect(() => {
        if (!inviteDepartment || !projectId) return;
        let cancelled = false;
        (async () => {
            setShareableLoading(true);
            setShareableError("");
            try {
                const { data } = await api.get(
                    `${shareableApiBase}/${projectId}/shareable-invite`,
                    { params: { department: inviteDepartment } },
                );
                if (!cancelled) setShareable(data);
            } catch (e) {
                if (!cancelled) {
                    setShareable(null);
                    setShareableError(
                        formatApiError(e) ||
                            "Department invite is temporarily unavailable. Try refreshing this page; if it keeps failing, contact support.",
                    );
                }
            } finally {
                if (!cancelled) setShareableLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [shareableApiBase, projectId, inviteDepartment]);

    const invitePathPrefix = isBetaWorkspace ? "/join/beta/" : "/join/";
    // Iter-158.13.2.7 — Dept context = ONLY the shareable URL.
    // Non-beta / non-dept surfaces retain the legacy project-level
    // link exactly as before. No client-side `?dept=` decoration.
    const legacyInviteLink = project?.invite_code
        ? `${window.location.origin}${invitePathPrefix}${project.invite_code}`
        : "";
    // Iter-158.13.2.7 P0 (from UAT) — Defence-in-depth against a
    // malformed backend `join_url`. Older backends (or a proxy that
    // strips Origin AND replays Referer) fed us URLs like
    //   https://host/beta/hm-app/projects/{id}/settings/join/beta/{CODE}
    // where the settings pathname leaked into the origin slot. Even
    // though the backend has now been fixed to compute origin
    // correctly, we sanitise defensively here so a stale deployment
    // can never re-emit a broken share link. We keep ONLY the
    // pathname + search of whatever the backend returned and reroot
    // it at the CURRENT window.location.origin. If the backend
    // returned a bare relative path, that is preserved as-is (still
    // resolvable inside the app).
    const normaliseShareableUrl = (raw) => {
        if (!raw || typeof raw !== "string") return "";
        try {
            const u = new URL(raw, window.location.origin);
            const path = u.pathname || "";
            const search = u.search || "";
            // Extract the trailing /join/beta/{code} even if the
            // pathname was polluted with prefix segments before the
            // /join/beta/ marker (defensive against Referer-leak).
            const idx = path.indexOf("/join/beta/");
            const cleanPath = idx >= 0 ? path.slice(idx) : path;
            return `${window.location.origin}${cleanPath}${search}`;
        } catch {
            return "";
        }
    };
    const inviteLink = inviteDepartment
        ? normaliseShareableUrl(shareable?.join_url)
        : legacyInviteLink;
    const copyInviteLink = async () => {
        if (!inviteLink) return;
        try {
            await navigator.clipboard.writeText(inviteLink);
            setLinkCopied(true);
            toast.success("Invite link copied");
            setTimeout(() => setLinkCopied(false), 1800);
        } catch {
            toast.error("Could not copy link");
        }
    };
    const rotateShareableLink = async () => {
        if (!inviteDepartment || !projectId || shareableRotating) return;
        setShareableRotating(true);
        try {
            const { data } = await api.post(
                `${shareableApiBase}/${projectId}/shareable-invite/rotate`,
                { department: inviteDepartment },
            );
            setShareable(data);
            setShareableError("");
            toast.success("New link minted. The previous link no longer works.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setShareableRotating(false);
        }
    };

    const startEditMemberName = (m) => {
        setEditingMemberId(m.user_id);
        setEditMemberName(m.name || "");
    };
    const cancelEditMemberName = () => {
        setEditingMemberId(null);
        setEditMemberName("");
    };
    const saveEditMemberName = async (m) => {
        const next = editMemberName.trim();
        if (!next || next === (m.name || "")) {
            cancelEditMemberName();
            return;
        }
        setEditMemberSaving(true);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/members/${m.user_id}`,
                { name: next },
            );
            setMembers((prev) =>
                prev.map((row) =>
                    row.user_id === m.user_id ? { ...row, name: data.name } : row,
                ),
            );
            toast.success(`Renamed to ${data.name}`);
            cancelEditMemberName();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setEditMemberSaving(false);
        }
    };

    // Iter-97 PART 9 — owner can promote/demote between admin and member.
    const [savingRoleId, setSavingRoleId] = useState(null);
    // Iter-156A.4 — confirmation now gates the actual PATCH. We
    // surface a friendly one-sentence summary of the new role so
    // the owner/admin sees exactly what they're committing to
    // before the change lands. Backend remains the source of truth.
    const requestRoleChange = (m, nextRole) => {
        const current = (m.role || "contributor").toLowerCase();
        if (current === nextRole) return;
        // Owner can't be changed via dropdown — that's the transfer
        // flow (handled separately).
        if (current === "owner" || nextRole === "owner") return;
        setPendingRoleChange({ member: m, nextRole });
    };

    const commitRoleChange = async () => {
        if (!pendingRoleChange) return;
        const { member: m, nextRole } = pendingRoleChange;
        setSavingRoleId(m.user_id);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/members/${m.user_id}/role`,
                { role: nextRole },
            );
            setMembers((prev) =>
                prev.map((row) =>
                    row.user_id === m.user_id ? { ...row, role: data.role } : row,
                ),
            );
            toast.success(
                `${m.name || m.email} is now ${
                    ROLE_LABELS[data.role] || data.role
                }`,
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingRoleId(null);
            setPendingRoleChange(null);
        }
    };

    // Iter-156A.4 — Remove member.
    const requestRemoveMember = (m) => setPendingRemove(m);
    const commitRemoveMember = async () => {
        if (!pendingRemove) return;
        const m = pendingRemove;
        setRemovingId(m.user_id);
        try {
            await api.delete(
                `${routeBase}/${projectId}/members/${m.user_id}`,
            );
            setMembers((prev) => prev.filter((row) => row.user_id !== m.user_id));
            toast.success(
                `${m.name || m.email} has been removed from the project`,
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRemovingId(null);
            setPendingRemove(null);
        }
    };

    // Iter-156A.4 — Transfer ownership.
    // Iter-158.13.2.10 — Split the POST from the follow-up refresh
    // so a network failure on the refetch does NOT masquerade as a
    // failed transfer. Previously any error in the Promise.all
    // (e.g. the previous owner briefly 404ing on GET project until
    // dept membership was materialised — see beta on_after_owner_
    // transfer hook) surfaced as a red "transfer failed" toast on
    // top of a transfer that had actually SUCCEEDED. The user then
    // saw contradictory state on reload. Fire the success toast the
    // moment /owner/transfer returns 200; the refetch is best-effort.
    const commitTransfer = async (newOwnerId) => {
        setTransferBusy(true);
        try {
            const { data: transferResult } = await api.post(
                `${apiBase}/${projectId}/owner/transfer`,
                {
                    new_owner_user_id: newOwnerId,
                    confirm: "transfer",
                },
            );
            // Success toast fires unconditionally on 200. Prefer the
            // server's echoed new_owner_name (authoritative) but
            // fall back to the members list if the field is empty.
            const memberFallback = (members || []).find(
                (m) => m.user_id === newOwnerId,
            );
            const newOwnerLabel =
                (transferResult && transferResult.new_owner_name) ||
                (memberFallback && (memberFallback.name || memberFallback.email)) ||
                "the new owner";
            toast.success(
                `Ownership transferred to ${newOwnerLabel}. You're now a Production Admin.`,
            );
            setTransferOpen(false);
            // Best-effort refresh — a failure here (stale role
            // cache, transient 404 on the newly-non-owner path,
            // etc.) is NOT a transfer failure.
            try {
                const [{ data: nextProj }, { data: nextMembers }] =
                    await Promise.all([
                        api.get(`${apiBase}/${projectId}`),
                        api.get(`${apiBase}/${projectId}/members`),
                    ]);
                setProject(_unwrapProject(nextProj));
                setMembers(nextMembers);
            } catch {
                // Refresh failed — the transfer itself succeeded.
                // Navigating away from Settings (or a page reload)
                // will pick up the new state from the server.
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTransferBusy(false);
        }
    };

    // Iter-156C Phase 3 — Script Access Control commit. Owner/Admin
    // PATCHes the visibility token; backend applies the change,
    // emits the audit event, and bounces back the fresh project doc.
    // We refresh local state and surface a calm confirmation toast.
    const [scriptVisBusy, setScriptVisBusy] = useState(false);
    const commitScriptVisibility = async (next) => {
        if (!project) return;
        const prev = project.script_visibility || "everyone";
        if (next === prev) return;
        setScriptVisBusy(true);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/script-visibility`,
                { visibility: next },
            );
            // Backend returns the updated project shape; merge so the
            // picker reflects the change without a full reload.
            setProject((p) => ({ ...(p || {}), ...data }));
            toast.success(
                next === "owner_admin"
                    ? "Script restricted to Administrators."
                    : "Script open to the Project Team.",
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setScriptVisBusy(false);
        }
    };

    // Iter-156A.5 — Leave Project. Available to every non-owner
    // (Contributor / Administrator / Viewer). Owners must transfer
    // ownership first — the UI surfaces a calm pointer to the
    // Transfer Ownership card rather than blocking with an error.
    const commitLeave = async () => {
        setLeaveBusy(true);
        try {
            await api.post(`${apiBase}/${projectId}/leave`);
            toast.success(
                `You've left ${project?.name || "this project"}. Your contributions stay on the project — attribution is preserved.`,
            );
            // Tiny delay so the toast registers before we navigate
            // away from the page.
            setTimeout(() => navigate("/projects"), 350);
        } catch (e) {
            toast.error(formatApiError(e));
            setLeaveBusy(false);
            setLeaveOpen(false);
        }
    };

    // Legacy alias used by the dropdown — now routes through the
    // confirmation gate above.
    const changeMemberRole = (m, nextRole) => requestRoleChange(m, nextRole);

    // Iter-158.13.2.20e.2 — Response normaliser lives at module
    // scope (`_unwrapProject`) so every fetch/refresh path uses the
    // same shape adapter regardless of mount.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`${apiBase}/${projectId}`);
                setProject(_unwrapProject(data));
            } catch (e) {
                setError(formatApiError(e));
            }
        })();
    }, [projectId, apiBase]);

    // Iter-157D — refresh project (and therefore role badges + gates)
    // when the tab gains focus or the global axios interceptor
    // observed a 403. This catches "admin demoted me to viewer in
    // another tab and I'm still seeing buttons" within one event
    // loop tick.
    const refreshProjectSilent = useCallback(async () => {
        try {
            const { data } = await api.get(`${apiBase}/${projectId}`);
            // Iter-157E P0(5)+(6) — Role transitions are toasted by
            // the global axios interceptor + App-level listener,
            // so we just absorb the fresh payload here.
            setProject(_unwrapProject(data));
            try {
                const { data: m } = await api.get(`${apiBase}/${projectId}/members`);
                setMembers(m);
            } catch (_) { /* members list is best-effort */ }
        } catch (_) { /* swallow — don't crash a background refresh */ }
    }, [projectId, apiBase]);
    useRefreshOnPermissionStale(refreshProjectSilent);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const canManage = isOwner || isAdmin;
    // Iter-158.13.2.20e.2 — Canonical Team & Access authority is
    // Production-scope. When ProjectSettings renders under the H&M
    // dept mount (apiBase === "/beta/hm-app/projects") the Production
    // T&A UI is a stale duplicate — every mutation on that mount
    // returns 403 now. Hide the Production presentation there; the
    // H&M-only dept-scope Team & Access section (settings-dept-members)
    // remains available. Users manage Production Team & Access from
    // the Production Hub → /beta/projects/:pid/settings.
    const isHmDeptScope = apiBase === "/beta/hm-app/projects";
    const canManageProductionTA = canManage && !isHmDeptScope;
    // Iter-158.13.2.20e.3 — legacy comment retired.
    // Iter-158.13.2.20e.4 — Storage/Restore Points are now
    // dual-mounted at Production scope (see beta_execute_restore
    // and _beta_prod_backup_project). This variable is used below to
    // suppress Production-owned surfaces on the H&M dept mount only.
    // Iter-158.13.2.20e.7 — Production-owned surfaces (Scripts setup,
    // Storage, Restore Points, Complete Export, Ownership Transfer,
    // Archive, Delete, and the Production Protection panel) are
    // rendered here so we can hide them on H&M dept scope with a
    // single derived flag. Nothing else changes — backend mounts,
    // endpoints and authority behaviour remain untouched.
    const isProductionScope = apiBase === "/beta/projects";
    // eslint-disable-next-line no-unused-vars
    const _isProductionScopeReserved = isProductionScope;
    // Iter-158.13.2.20e.7 — Scripts setup UI is Production-owned and
    // has a dedicated page at `/beta/projects/:pid/script-setup`.
    // Under Route 2 scope (Production or H&M dept) it should not
    // duplicate itself inside Settings. Legacy `/projects/:pid/settings`
    // keeps the historical inline setup surface.
    const showInlineScriptsGroup = canManage && !isBetaWorkspace;
    const canManageProductionOnly = canManage && !isHmDeptScope;
    const canOwnProductionOnly = isOwner && !isHmDeptScope;
    // Iter-158.13.2.16 — Department privilege state (Hair & Makeup).
    // Independent of production canManage; keys off the caller's
    // explicit dept role (Owner override folded in server-side via
    // `dept_role`).
    const deptRole = (project?.dept_role || "").toLowerCase();
    const isDeptAdmin = deptRole === "admin";
    const canManageDept = isDeptAdmin;
    const [deptMembers, setDeptMembers] = useState([]);
    const [deptBusy, setDeptBusy] = useState("");
    const refreshDeptMembers = useCallback(async () => {
        if (apiBase !== "/beta/hm-app/projects") return;
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/dept-members`);
            setDeptMembers(Array.isArray(data?.members) ? data.members : []);
        } catch (_) { /* swallow */ }
    }, [apiBase, projectId]);
    useEffect(() => {
        refreshDeptMembers();
    }, [refreshDeptMembers]);
    const changeDeptRole = async (targetUid, nextRole) => {
        setDeptBusy(targetUid);
        try {
            await api.patch(
                `${apiBase}/${projectId}/dept-members/${targetUid}`,
                { role: nextRole },
            );
            toast.success(`Hair & Makeup role updated to ${nextRole}.`);
            refreshDeptMembers();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeptBusy("");
        }
    };
    const removeDeptMember = async (targetUid, label) => {
        if (!window.confirm(`Remove ${label || "this member"} from Hair & Makeup? Their Production role stays.`)) return;
        setDeptBusy(targetUid);
        try {
            await api.delete(
                `${apiBase}/${projectId}/dept-members/${targetUid}`,
            );
            toast.success("Removed from Hair & Makeup.");
            refreshDeptMembers();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeptBusy("");
        }
    };
    // Iter-156A.5 — archive lock affordances on the FE. Source of
    // truth remains the server (any write attempt is 403'd by the
    // archive write-lock middleware) but we hide the buttons in
    // advance so users don't start flows they can't complete.
    const isArchived = Boolean(project?.archived);
    const canManageNow = canManage && !isArchived;

    // Iter-156A.5 — Leave Project state. Available to every non-owner
    // member (Contributor / Administrator / Viewer). Owner cannot
    // leave until they transfer ownership.
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [leaveBusy, setLeaveBusy] = useState(false);

    useEffect(() => {
        if (!canManage) return;
        (async () => {
            setPermLoading(true);
            try {
                const { data: m } = await api.get(`${apiBase}/${projectId}/members`);
                setMembers(m);
            } catch (e) {
                setError(formatApiError(e));
            } finally {
                setPermLoading(false);
            }
        })();
    }, [canManage, projectId, apiBase]);

    const togglePermission = async (member, key, next) => {
        if (member.role === "owner") return;
        setPermPendingMember(member.user_id);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/members/${member.user_id}/permissions`,
                { [key]: next },
            );
            setMembers((prev) =>
                prev.map((m) =>
                    m.user_id === member.user_id
                        ? { ...m, permissions: data.permissions }
                        : m,
                ),
            );
            toast.success(
                next
                    ? `Permission granted to ${member.name || member.email}`
                    : `Permission revoked from ${member.name || member.email}`,
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPermPendingMember(null);
        }
    };

    const nameOk = project && confirmText.trim() === project.name;

    const resetInvite = () => {
        setInviteName("");
        setInviteEmail("");
        setInviteError("");
        setInvitePreview(null);
        setInviteCopied(false);
        setInviteSubmitting(false);
    };

    const submitInvite = async (e) => {
        e?.preventDefault?.();
        if (inviteSubmitting) return;
        const name = inviteName.trim();
        const email = inviteEmail.trim();
        if (!name || !email) {
            setInviteError("Name and email are required.");
            return;
        }
        setInviteSubmitting(true);
        setInviteError("");
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/invitations`,
                inviteDepartment
                    ? { name, email, department: inviteDepartment }
                    : { name, email },
            );
            setInvitePreview(data);
            setInviteCopied(false);
            toast.success(`Invitation ready for ${data.recipient_name}`);
        } catch (err) {
            const detail = formatApiError(err);
            setInviteError(detail);
            toast.error(detail);
        } finally {
            setInviteSubmitting(false);
        }
    };

    const copyInvite = async () => {
        if (!invitePreview?.message) return;
        try {
            await navigator.clipboard.writeText(invitePreview.message);
            setInviteCopied(true);
            toast.success("Invitation copied to clipboard");
            setTimeout(() => setInviteCopied(false), 2000);
        } catch {
            // Fallback for browsers without clipboard permission — select-and-copy hint.
            toast.error("Couldn't copy. Long-press the text to copy manually.");
        }
    };

    const openInviteDialog = () => {
        resetInvite();
        setInviteOpen(true);
    };

    const closeInviteDialog = (next) => {
        if (inviteSubmitting) return;
        setInviteOpen(next);
        if (!next) {
            // Delay reset so the dialog close animation doesn't flicker.
            setTimeout(resetInvite, 200);
        }
    };

    const doDelete = async () => {
        if (!nameOk) return;
        setDeleting(true);
        setError("");
        try {
            await api.delete(`${apiBase}/${projectId}`, {
                data: { confirm_name: confirmText.trim() },
            });
            toast.success("Project deleted");
            navigate("/", { replace: true });
        } catch (e) {
            setError(formatApiError(e));
            setDeleting(false);
        }
    };

    return (
        <div data-testid="project-settings-page" className="min-h-screen">
            <ProjectHeader
                projectName={project?.name}
                subtitle={isProductionScope ? "Production Management" : "Settings"}
            />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                <div className="mb-6">
                    {/* Iter-157O.2 — "Project settings" eyebrow removed
                        per pilot polish. The page now leads with just
                        the project name; the page identity is already
                        carried by the header subtitle + nav state.
                        Iter-158.13.2.20e.11 — on Production scope the
                        header subtitle reads "Production Management"
                        so the surface is presented as the deliberate
                        administrative area (Storage, Restore, Export,
                        Ownership, Archive/Delete) rather than a peer
                        working destination. H&M workspace + legacy
                        Settings retain the "Settings" subtitle. */}
                    <h1 className="font-display text-3xl font-black text-white">
                        {project?.name || "—"}
                    </h1>
                    {isProductionScope ? (
                        <p
                            data-testid="settings-production-management-eyebrow"
                            className="mt-1 text-[11px] uppercase tracking-[0.24em] text-neutral-500"
                        >
                            Production Management
                        </p>
                    ) : null}
                </div>

                {error ? (
                    <p data-testid="settings-error" className="text-sm text-red-400 mb-4">
                        {error}
                    </p>
                ) : null}

                {/* Iter-157N — Production Protection introduces
                    Settings, not another section. Moved to the very
                    top so users see the reassurance layer (your
                    production owns its data...) BEFORE the operational
                    mechanics. The Storage / Restore Points / Export /
                    Governance detail cards still live in their
                    canonical positions below; this card is the trust
                    banner that establishes psychological safety
                    before users encounter those mechanics.
                    Iter-158.13.2.20e.5 — heading + copy updated to
                    reflect the Production/Department restructure:
                    "Production Protection", "Return — Restore the
                    production later", and quick-jump chips still
                    point to the current Production-level sections
                    (Storage, Restore Points, Export, Governance).
                    No new controls; navigation housekeeping only.
                    Iter-158.13.2.20e.7 — hidden entirely on H&M
                    dept scope: those Production-owned surfaces no
                    longer render on H&M Settings, so the chips would
                    have pointed at nothing and the trust promise
                    belongs at Production. */}
                {/* Iter-158.13.2.20f.5 #7 — retired the "Production
                    Protection" shortcut card that used to render here.
                    The card carried Storage / Restore Points / Export /
                    Governance jump chips that duplicated navigation
                    already present in the Production Management hub
                    (`/beta/projects/:pid`) and the section anchors below
                    on this page. Presentation cleanup only — every
                    downstream section (Storage / Restore / Export /
                    Governance) still renders exactly as before. */}

                {/* Iter-157F — Group 1: Production Overview. Answers
                    "What production am I in?". Merged the old
                    standalone System / Appearance block in here so
                    the user sees identity + theme together at the
                    top. Iter-158.13.2.20e.5 — renamed Project → Production
                    to reflect the current Production/Department scope. */}
                <SectionGroupHeader
                    index={1}
                    title="Production overview"
                    question="What production am I in?"
                    testid="settings-group-overview"
                />
                <section
                    data-testid="settings-general"
                    className="rounded-xl border border-white/10 bg-card p-5 space-y-4 mb-5"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                            Production overview
                        </h2>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                            Project name
                        </div>
                        <div className="text-white font-semibold mt-1">{project?.name || "—"}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                            Production type
                        </div>
                        <div className="text-white mt-1">
                            {PRODUCTION_TYPE_LABELS[project?.production_type] ||
                                project?.production_type ||
                                "—"}
                        </div>
                    </div>
                    {/* Iter-158.13.2.20e.10 follow-up — Production
                        Overview must be Department-neutral. The
                        hardcoded "Department: Hair & Makeup" tile
                        is a legacy leak from the pre-Route-2 H&M
                        Settings page. Hide it on Production scope;
                        H&M workspace + legacy mounts keep it. */}
                    {isProductionScope ? null : (
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                            Department
                        </div>
                        <div
                            data-testid="settings-department"
                            className="text-white mt-1 text-sm"
                        >
                            Hair &amp; Makeup
                        </div>
                    </div>
                    )}
                    {/* Iter-158.13.2.16 — Two-scope role presentation.
                        Replaces the single ambiguous "Your role" tile
                        with side-by-side Production Role and
                        Hair & Makeup Role. Reads project.role (prod
                        scope) and project.dept_role (dept scope) —
                        both surfaced additively by
                        `hmfull_project_detail` since 13.2.14. Owner
                        oversight is folded into dept_role upstream
                        so an Owner with an explicit dept role
                        (contributor/viewer) sees "Administrator
                        access · Production Owner" to distinguish the
                        effective override from the underlying role.
                        Iter-158.13.2.20e.7 — role tiles wait for
                        `project` to arrive so the loading placeholder
                        no longer misreads Owner as "Contributor".
                        The H&M role tile now presents `dept_role == null`
                        neutrally as "Not a member of Hair & Makeup"
                        so Production Owner oversight is not misread
                        as explicit H&M membership. */}
                    {project ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                Production role
                            </div>
                            <div
                                data-testid="settings-role-production"
                                className="mt-1 inline-flex items-center gap-2 rounded-full bg-secondary border border-white/10 px-3 py-1 text-xs text-white"
                            >
                                {isOwner ? (
                                    <>
                                        <ShieldCheck size={12} className="text-emerald-300" />
                                        Production Owner
                                    </>
                                ) : isAdmin ? (
                                    <>
                                        <ShieldCheck size={12} className="text-amber-200" />
                                        Production Admin
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck size={12} className="text-neutral-400" />
                                        Production Contributor
                                    </>
                                )}
                            </div>
                            <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                                {isOwner
                                    ? "Full production governance. Invite, transfer, archive, delete."
                                    : isAdmin
                                      ? "Governs the whole production. Manage members, edit production settings and canonical script."
                                      : "Production-level participation. Cannot manage users or governance."}
                            </p>
                        </div>
                        {/* Iter-158.13.2.20e.10 follow-up — Production
                            Overview must be Department-neutral. Hide
                            the Hair & Makeup role tile on Production
                            scope; H&M workspace + legacy mounts keep
                            it visible byte-identically. */}
                        {isProductionScope ? null : (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                Hair &amp; Makeup role
                            </div>
                            {(() => {
                                // Iter-158.13.2.20e.7 — dept_role
                                // presence is the source of truth for
                                // explicit H&M membership. `null` /
                                // empty string means the user is NOT a
                                // Hair & Makeup member — including a
                                // Production Owner whose authority
                                // over H&M is derived from Production
                                // ownership, NOT explicit membership.
                                // Preserve the underlying Owner
                                // override behaviour (the backend
                                // already resolves the override into
                                // dept_role="admin" when appropriate);
                                // here we only fix the FE label so
                                // authority and membership stay
                                // strictly distinct in copy.
                                const rawDeptRole = project?.dept_role;
                                const hasDeptMembership =
                                    typeof rawDeptRole === "string"
                                    && rawDeptRole.trim() !== "";
                                const deptRole = (rawDeptRole || "").toLowerCase();
                                const isDeptAdmin = deptRole === "admin";
                                const isDeptViewer = deptRole === "viewer";
                                const isDeptContributor = deptRole === "contributor";
                                return (
                                    <>
                                        <div
                                            data-testid="settings-role-department"
                                            className="mt-1 inline-flex items-center gap-2 rounded-full bg-secondary border border-white/10 px-3 py-1 text-xs text-white"
                                        >
                                            <ShieldCheck
                                                size={12}
                                                className={
                                                    isDeptAdmin
                                                        ? "text-amber-200"
                                                        : isDeptViewer
                                                          ? "text-neutral-500"
                                                          : hasDeptMembership
                                                            ? "text-neutral-400"
                                                            : "text-neutral-600"
                                                }
                                            />
                                            {!hasDeptMembership
                                                ? "Not a member of Hair & Makeup"
                                                : isDeptAdmin
                                                    ? "Hair & Makeup Admin"
                                                    : isDeptViewer
                                                      ? "Hair & Makeup Viewer"
                                                      : "Hair & Makeup Contributor"}
                                        </div>
                                        <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                                            {!hasDeptMembership
                                                ? (isOwner
                                                    ? "You are not an explicit Hair & Makeup member. As Production Owner you retain oversight authority when required, but this is not the same as Department membership."
                                                    : "You are not an explicit Hair & Makeup member.")
                                                : isDeptAdmin
                                                    ? "Administer this department — invite, promote, demote, remove members and use every H&M capability."
                                                    : isDeptViewer
                                                      ? "Read-only inside Hair & Makeup. Cannot add or edit."
                                                      : isDeptContributor
                                                        ? "Contribute to Hair & Makeup — add and edit characters, scenes, continuity notes and cast. Cannot delete or manage members."
                                                        : "Hair & Makeup department member."}
                                        </p>
                                    </>
                                );
                            })()}
                        </div>
                        )}
                    </div>
                    ) : null}
                    {/* Iter-157F — Appearance row consolidated INTO
                        Project Overview so identity + canvas live
                        together in the very first block. Same
                        ThemeToggle + copy as the old standalone
                        System section. */}
                    <div className="pt-4 border-t border-white/5">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                    Appearance
                                </div>
                                <p className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                                    Switch to a lighter canvas for use in
                                    bright environments — trailers, exteriors,
                                    on-set marquees in daylight.
                                </p>
                            </div>
                            <ThemeToggle testid="settings-theme-toggle" label={false} />
                        </div>
                    </div>
                </section>

                {/* Iter-157F — Group 2: Scripts.
                    Iter-158.13.2.20e.7 — header follows the inline
                    Scripts group. On Route 2 the canonical setup lives
                    at /beta/projects/:pid/script-setup, so both the
                    header and the inline surface are suppressed off
                    legacy scope. */}
                {showInlineScriptsGroup ? (
                    <SectionGroupHeader
                        index={2}
                        title="Scripts"
                        question="How is the screenplay managed?"
                        testid="settings-group-scripts"
                    />
                ) : null}

                {/* Iter-157F.2 — Settings Calm-Down Pass: Scripts
                    collapses into a compact summary once the
                    project is in a healthy operational state
                    (script uploaded + scenes & cast built + no
                    build / review pending). The wrapper fetches
                    /scripts-summary and toggles between the
                    compact card and the full management surface
                    below. Onboarding states (no script, in-build,
                    failed, review-required) stay expanded.
                    Iter-158.13.2.20e.7 — under Route 2 (Production
                    or H&M dept) the canonical Script Setup lives at
                    a dedicated route (`/beta/projects/:pid/script-setup`)
                    and this inline surface would duplicate it, so
                    it renders only on legacy `/projects` scope. */}
                {showInlineScriptsGroup ? (
                    <ScriptsCollapsibleGroup projectId={projectId}>
                        <section
                            data-testid="settings-script-setup"
                            className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-primary/40"
                        >
                        <div className="flex items-center gap-2 mb-1">
                            <FileText size={14} className="text-primary" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                                Scripts
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-2 leading-relaxed">
                            Upload your screenplay and we&apos;ll build the scenes &amp; cast for you.
                            This is a setup step — once built, the team uses the{" "}
                            <strong className="text-white font-semibold">Script</strong>{" "}
                            tile from the project home to read the script day-to-day.
                            {project?.production_type === "tv_series" ? (
                                <>
                                    {" "}
                                    Episodic series get one script slot per
                                    episode — add as many as you need.
                                </>
                            ) : null}
                        </p>
                        <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                            Revised pages and full-script revisions are
                            managed inline below — pages stay non-destructive
                            and continuity follows the original scene UUIDs.
                        </p>
                        <div className="mt-4 -mx-1">
                            {project?.production_type === "tv_series" ? (
                                <EpisodicScriptSetup projectId={projectId} />
                            ) : (
                                <ScriptSection projectId={projectId} />
                            )}
                        </div>
                        </section>

                        {/* Iter-158.13.2.20e.5 — Script Access moved OUT of the
                            Scripts group and into Team & Access (see
                            `settings-script-access` further down). The
                            canonical Script is Production-owned; authority
                            over who may READ it belongs alongside the other
                            Production Team & Access controls, not next to
                            script setup/management. */}
                    </ScriptsCollapsibleGroup>
                ) : null}

                {/* Iter-157F — Group 3: Team & Access. */}
                {canManageProductionTA ? (
                    <SectionGroupHeader
                        index={3}
                        title="Team & access"
                        question="Who can access this production, and what can they do?"
                        testid="settings-group-team"
                    />
                ) : null}

                {canManageProductionTA ? (
                    <section
                        data-testid="settings-invite"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-emerald-400/40"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <UserPlus size={14} className="text-emerald-300" />
                            {/* Iter-134 / Iter-157C — first section is the
                                invite + join surface; renamed from
                                "Team & access" to "Invite & join" so the
                                two distinct surfaces (Invite & join vs.
                                Team & access) no longer share an identical
                                heading and confuse pilot users. */}
                            <h2
                                data-testid="settings-invite-join"
                                className="text-xs uppercase tracking-[0.25em] text-neutral-400"
                            >
                                Invite &amp; join
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-2 leading-relaxed">
                            Invite teammates with the project QR or a copyable
                            invite link, then tune per-member permissions
                            below. The invite code lives only here — share it
                            from a single place.
                        </p>

                        {/* Iter-116 — full invite tools consolidated here.
                            QR code + copy link + visible URL + per-recipient
                            invitation generator. Previously these were
                            duplicated on ProjectHome; the Overview is now
                            focused on working surfaces only. */}
                        <div className="mt-4 rounded-lg border border-white/10 bg-card/40 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        {inviteDepartment ? "Department invite code" : "Invite code"}
                                    </div>
                                    <div
                                        data-testid="settings-invite-code"
                                        className="mono text-2xl font-semibold text-white tracking-[0.25em] mt-1 break-all"
                                    >
                                        {inviteDepartment
                                            ? (shareable?.code
                                                || (shareableLoading
                                                    ? "…"
                                                    : (shareableError ? "unavailable" : "------")))
                                            : (project?.invite_code || "------")}
                                    </div>
                                    {/* Iter-158.13.2.7 — visible error surface
                                        for a broken dept invite. Replaces the
                                        old silent T1 fallback. */}
                                    {inviteDepartment && shareableError && !shareable ? (
                                        <p
                                            data-testid="settings-invite-error"
                                            className="text-[11px] text-amber-300 mt-1.5"
                                        >
                                            {shareableError}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
                                    <button
                                        data-testid="settings-show-qr"
                                        onClick={() => setQrOpen(true)}
                                        title="Show QR code"
                                        className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-primary text-primary-foreground hover:bg-primary/90"
                                    >
                                        <QrCode size={14} />
                                        <span className="text-xs font-medium">QR</span>
                                    </button>
                                    <button
                                        data-testid="settings-copy-invite-link"
                                        onClick={copyInviteLink}
                                        className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-secondary text-white hover:bg-white/10"
                                    >
                                        {linkCopied ? (
                                            <Check size={14} />
                                        ) : (
                                            <Copy size={14} />
                                        )}
                                        <span className="text-xs font-medium">
                                            {linkCopied ? "Copied" : "Copy link"}
                                        </span>
                                    </button>
                                    {inviteDepartment ? (
                                        <button
                                            data-testid="settings-rotate-shareable-link"
                                            onClick={rotateShareableLink}
                                            disabled={shareableRotating || shareableLoading}
                                            title="Regenerate the department shareable link. The previous URL and QR will stop working immediately."
                                            className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {shareableRotating ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <QrCode size={14} />
                                            )}
                                            <span className="text-xs font-medium">
                                                {shareableRotating ? "Rotating…" : "Regenerate"}
                                            </span>
                                        </button>
                                    ) : (isOwner ? (
                                        // Iter-158.13.2.20f.1 (#6) —
                                        // Production Owner Regenerate.
                                        // Reuses the same endpoint the
                                        // QR dialog calls.
                                        <button
                                            data-testid="settings-rotate-production-invite"
                                            onClick={async () => {
                                                const ok = window.confirm(
                                                    "Regenerate the Production invite code?\n\nThe current QR code and any links you've already shared will stop working immediately. Existing members keep their access.",
                                                );
                                                if (!ok) return;
                                                try {
                                                    const { data } = await api.post(
                                                        `${apiBase}/${projectId}/invite-code/regenerate`,
                                                    );
                                                    if (data?.invite_code) {
                                                        setProject((prev) => prev
                                                            ? { ...prev, invite_code: data.invite_code }
                                                            : prev);
                                                    }
                                                    toast.success("Invite code regenerated");
                                                } catch (e) {
                                                    toast.error(formatApiError(e));
                                                }
                                            }}
                                            title="Regenerate the Production invite code. The previous URL and QR will stop working immediately."
                                            className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30"
                                        >
                                            <QrCode size={14} />
                                            <span className="text-xs font-medium">Regenerate</span>
                                        </button>
                                    ) : null)}
                                </div>
                            </div>
                            <div
                                data-testid="settings-invite-link"
                                className="mono text-[11px] text-neutral-500 mt-3 truncate"
                            >
                                {inviteLink}
                            </div>
                        </div>

                        <Button
                            data-testid="settings-invite-open"
                            onClick={openInviteDialog}
                            className="mt-4 min-tap bg-emerald-500 hover:bg-emerald-500/90 text-neutral-900 font-semibold"
                        >
                            <UserPlus size={14} className="mr-2" />
                            Generate personalised invitation
                        </Button>
                    </section>
                ) : null}

                {canManageProductionTA ? (
                    <section
                        data-testid="settings-members"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <UserCog size={14} className="text-neutral-400" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                                Team &amp; access · Production privileges
                            </h2>
                        </div>
                        <p
                            data-testid="settings-members-intro"
                            className="text-xs text-neutral-400 mb-3 leading-relaxed"
                        >
                            Production-level privileges — who governs the whole production.
                            Production Admins (your HODs / producers), Production Contributors
                            (the working crew) and Production Viewers (anyone who just needs to see
                            the continuity record). Department privileges (Hair & Makeup, Costume,
                            Art, VFX & AI) are separate — they&apos;re managed inside each department
                            and don&apos;t follow the production role.
                        </p>

                        {/* Iter-157C — Transfer Ownership moved out of this
                            section into its own Governance card below.
                            Day-to-day team management lives here; the
                            irreversible governance action lives separately. */}
                        {permLoading && members.length === 0 ? (
                            <p className="text-sm text-neutral-400">Loading members…</p>
                        ) : (
                            <ul
                                data-testid="settings-member-list"
                                className="space-y-2"
                            >
                                {members.map((m) => {
                                    const isOwnerRow = m.role === "owner";
                                    const perms = m.permissions || {};
                                    const renderToggle = (key, label, testid) => (
                                        <label
                                            key={key}
                                            className="flex items-center justify-between gap-3 rounded-md bg-black/15 px-2.5 py-2"
                                        >
                                            <span className="text-[11px] text-neutral-300">
                                                {label}
                                            </span>
                                            <Switch
                                                data-testid={`${testid}-${m.user_id}`}
                                                checked={Boolean(perms[key])}
                                                disabled={
                                                    isOwnerRow ||
                                                    permPendingMember === m.user_id
                                                }
                                                onCheckedChange={(v) =>
                                                    togglePermission(m, key, v)
                                                }
                                            />
                                        </label>
                                    );
                                    return (
                                        <li
                                            key={m.user_id}
                                            data-testid={`settings-member-${m.user_id}`}
                                            className="rounded-lg border border-white/10 bg-secondary/40 p-3"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    {editingMemberId === m.user_id ? (
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                data-testid={`settings-member-name-input-${m.user_id}`}
                                                                value={editMemberName}
                                                                onChange={(e) => setEditMemberName(e.target.value)}
                                                                disabled={editMemberSaving}
                                                                autoFocus
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") saveEditMemberName(m);
                                                                    if (e.key === "Escape") cancelEditMemberName();
                                                                }}
                                                                className="h-8 text-sm bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                                            />
                                                            <button
                                                                type="button"
                                                                data-testid={`settings-member-name-save-${m.user_id}`}
                                                                onClick={() => saveEditMemberName(m)}
                                                                disabled={editMemberSaving}
                                                                className="tap inline-flex items-center justify-center h-8 w-8 rounded-md text-emerald-300 hover:text-white hover:bg-emerald-500/15 disabled:opacity-50"
                                                                aria-label="Save name"
                                                            >
                                                                {editMemberSaving ? (
                                                                    <Loader2 size={13} className="animate-spin" />
                                                                ) : (
                                                                    <Check size={13} />
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                data-testid={`settings-member-name-cancel-${m.user_id}`}
                                                                onClick={cancelEditMemberName}
                                                                disabled={editMemberSaving}
                                                                className="tap inline-flex items-center justify-center h-8 w-8 rounded-md text-neutral-400 hover:text-white hover:bg-white/5"
                                                                aria-label="Cancel"
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                data-testid={`settings-member-name-${m.user_id}`}
                                                                className="text-white font-medium truncate"
                                                            >
                                                                {m.name || m.email}
                                                            </div>
                                                            {!isOwnerRow ? (
                                                                <button
                                                                    type="button"
                                                                    data-testid={`settings-member-name-edit-${m.user_id}`}
                                                                    onClick={() => startEditMemberName(m)}
                                                                    aria-label={`Edit ${m.name || m.email}'s display name`}
                                                                    className="tap inline-flex items-center justify-center h-7 w-7 rounded-md text-neutral-500 hover:text-white hover:bg-white/5"
                                                                >
                                                                    <Pencil size={12} />
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                    <div className="text-xs text-neutral-400 truncate">
                                                        {m.email}
                                                        {isOwnerRow ? (
                                                            <span
                                                                data-testid={`settings-member-role-${m.user_id}`}
                                                                className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-300"
                                                            >
                                                                <ShieldCheck size={10} /> {ROLE_LABELS.owner}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                data-testid={`settings-member-role-${m.user_id}`}
                                                                className={`ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] ${
                                                                    m.role === "admin"
                                                                        ? "text-amber-200"
                                                                        : m.role === "viewer"
                                                                            ? "text-sky-300"
                                                                            : "text-neutral-400"
                                                                }`}
                                                            >
                                                                <ShieldCheck size={10} />
                                                                {ROLE_LABELS[m.role] || ROLE_LABELS.contributor}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {!isOwnerRow && canManageNow ? (
                                                <div
                                                    data-testid={`settings-member-role-row-${m.user_id}`}
                                                    className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-secondary/30 p-2"
                                                >
                                                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 shrink-0">
                                                        Role
                                                    </span>
                                                    <select
                                                        data-testid={`settings-member-role-select-${m.user_id}`}
                                                        value={(m.role && m.role !== "member") ? m.role : "contributor"}
                                                        disabled={savingRoleId === m.user_id || (isAdmin && m.role === "admin")}
                                                        title={
                                                            isAdmin && m.role === "admin"
                                                                ? "Administrators can't change another administrator's role. Ask the project owner."
                                                                : undefined
                                                        }
                                                        onChange={(e) => changeMemberRole(m, e.target.value)}
                                                        className="flex-1 min-w-[140px] h-8 px-2 rounded-md text-xs bg-card border border-white/10 focus:ring-1 focus:ring-primary focus:outline-none disabled:opacity-50"
                                                    >
                                                        <option value="admin">Admin</option>
                                                        <option value="contributor">Contributor</option>
                                                        <option value="viewer">Viewer</option>
                                                    </select>
                                                    {savingRoleId === m.user_id ? (
                                                        <Loader2 size={12} className="animate-spin text-neutral-400" />
                                                    ) : null}
                                                    {/* Iter-156A.4 — Remove member.
                                                        Iter-156A.5 — hidden on archived projects (write-lock applies). */}
                                                    {isOwner || (isAdmin && (m.role !== "admin")) ? (
                                                        <button
                                                            type="button"
                                                            data-testid={`settings-member-remove-${m.user_id}`}
                                                            disabled={removingId === m.user_id}
                                                            onClick={() => requestRemoveMember(m)}
                                                            className="tap inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs border border-red-500/30 bg-red-500/5 hover:bg-red-500/15 text-red-200 disabled:opacity-50"
                                                            title="Remove this person from the project"
                                                        >
                                                            {removingId === m.user_id ? (
                                                                <Loader2 size={12} className="animate-spin" />
                                                            ) : (
                                                                <Trash2 size={12} />
                                                            )}
                                                            Remove
                                                        </button>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                            {/* Iter-156A.4 — Plain "what this role can do" hint replaces
                                                the previous 7-toggle grid as the default view. The
                                                advanced toggles still exist for edge cases and live
                                                behind the disclosure below. */}
                                            {!isOwnerRow ? (
                                                <div
                                                    data-testid={`settings-member-role-hint-${m.user_id}`}
                                                    className="mt-2 text-[12px] text-neutral-300 leading-relaxed"
                                                >
                                                    {ROLE_DESCRIPTIONS[m.role] || ROLE_DESCRIPTIONS.contributor}
                                                </div>
                                            ) : null}
                                            {!isOwnerRow && m.role === "contributor" ? (
                                                <details
                                                    data-testid={`settings-member-advanced-${m.user_id}`}
                                                    className="mt-3 rounded-md border border-white/5 bg-secondary/20 group"
                                                >
                                                    <summary className="cursor-pointer list-none px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200">
                                                        Advanced permissions
                                                        <span className="ml-2 text-[10px] normal-case tracking-normal text-neutral-500">
                                                            (rarely needed — most Contributors should keep the defaults)
                                                        </span>
                                                    </summary>
                                                    <div className="px-2 pb-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                        <div
                                                            data-testid={`settings-member-perms-scenes-${m.user_id}`}
                                                            className="rounded-md border border-white/10 p-2 space-y-1.5"
                                                        >
                                                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
                                                                Scenes
                                                            </div>
                                                            {renderToggle(
                                                                "scenes_add",
                                                                "Can add scenes",
                                                                "settings-member-toggle-scenes-add",
                                                            )}
                                                            {renderToggle(
                                                                "scenes_edit",
                                                                "Can edit scenes",
                                                                "settings-member-toggle-scenes-edit",
                                                            )}
                                                            {renderToggle(
                                                                "scenes_delete",
                                                                "Can delete scenes",
                                                                "settings-member-toggle-scenes-delete",
                                                            )}
                                                        </div>
                                                        <div
                                                            data-testid={`settings-member-perms-cast-${m.user_id}`}
                                                            className="rounded-md border border-white/10 p-2 space-y-1.5"
                                                        >
                                                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
                                                                Cast
                                                            </div>
                                                            {renderToggle(
                                                                "cast_add",
                                                                "Can add cast",
                                                                "settings-member-toggle-cast-add",
                                                            )}
                                                            {renderToggle(
                                                                "cast_edit",
                                                                "Can edit cast",
                                                                "settings-member-toggle-cast-edit",
                                                            )}
                                                            {renderToggle(
                                                                "cast_delete",
                                                                "Can delete cast",
                                                                "settings-member-toggle-cast-delete",
                                                            )}
                                                        </div>
                                                    </div>
                                                </details>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                ) : null}

                {/* Iter-158.13.2.20e.7 — Department invitation surface.
                    Restored above H&M Team & Access so H&M
                    Administrators can invite people INTO Hair &
                    Makeup independently of Production administration
                    — Copy Link, QR, Email invite and rotate the
                    shareable link. Consumes the SAME already-wired
                    department-shareable-invite state (`shareable`,
                    `rotateShareableLink`, `openInviteDialog`) that
                    used to live inside the Production Team & Access
                    invite panel. No new backend endpoints; no new
                    permission model. Hidden on non-H&M scope.
                    Gate on `isDeptAdmin` (Admins invite) or Production
                    Owner override folded upstream (dept_role === "admin"
                    already includes the Owner override per
                    hmfull_project_detail). */}
                {isHmDeptScope && canManageDept ? (
                    <section
                        data-testid="settings-dept-invite"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-emerald-400/40"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <UserPlus size={14} className="text-emerald-300" />
                            <h2
                                data-testid="settings-dept-invite-heading"
                                className="text-xs uppercase tracking-[0.25em] text-neutral-400"
                            >
                                Invite &amp; join · Hair &amp; Makeup
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-2 leading-relaxed">
                            Invite teammates into Hair &amp; Makeup with the
                            department QR or a copyable shareable link. Joining
                            through this surface lands people directly in the
                            H&amp;M workspace as Contributors.
                        </p>
                        <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">
                            Independent from Production invitations. Anyone who
                            joins here is added to Hair &amp; Makeup only;
                            Production membership is managed by the Production
                            Owner or Admin.
                        </p>
                        <div className="mt-4 rounded-lg border border-white/10 bg-card/40 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        Department invite code
                                    </div>
                                    <div
                                        data-testid="settings-dept-invite-code"
                                        className="mono text-2xl font-semibold text-white tracking-[0.25em] mt-1 break-all"
                                    >
                                        {shareable?.code
                                            || (shareableLoading
                                                ? "…"
                                                : (shareableError ? "unavailable" : "------"))}
                                    </div>
                                    {shareableError && !shareable ? (
                                        <p
                                            data-testid="settings-dept-invite-error"
                                            className="text-[11px] text-amber-300 mt-1.5"
                                        >
                                            {shareableError}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
                                    <button
                                        data-testid="settings-dept-invite-qr"
                                        onClick={() => setQrOpen(true)}
                                        title="Show department QR code"
                                        className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-primary text-primary-foreground hover:bg-primary/90"
                                    >
                                        <QrCode size={14} />
                                        <span className="text-xs font-medium">QR</span>
                                    </button>
                                    <button
                                        data-testid="settings-dept-invite-copy-link"
                                        onClick={copyInviteLink}
                                        className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-secondary text-white hover:bg-white/10"
                                    >
                                        {linkCopied ? (
                                            <Check size={14} />
                                        ) : (
                                            <Copy size={14} />
                                        )}
                                        <span className="text-xs font-medium">
                                            {linkCopied ? "Copied" : "Copy link"}
                                        </span>
                                    </button>
                                    <button
                                        data-testid="settings-dept-invite-rotate"
                                        onClick={rotateShareableLink}
                                        disabled={shareableRotating || shareableLoading}
                                        title="Regenerate the department shareable link. The previous URL and QR will stop working immediately."
                                        className="tap min-tap inline-flex items-center justify-center gap-2 rounded-md px-3 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {shareableRotating ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <QrCode size={14} />
                                        )}
                                        <span className="text-xs font-medium">
                                            {shareableRotating ? "Rotating…" : "Regenerate"}
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <div
                                data-testid="settings-dept-invite-link"
                                className="mono text-[11px] text-neutral-500 mt-3 truncate"
                            >
                                {inviteLink}
                            </div>
                        </div>
                        <Button
                            data-testid="settings-dept-invite-email"
                            onClick={openInviteDialog}
                            className="mt-4 min-tap bg-emerald-500 hover:bg-emerald-500/90 text-neutral-900 font-semibold"
                        >
                            <UserPlus size={14} className="mr-2" />
                            Invite by email
                        </Button>
                    </section>
                ) : null}

                {/* Iter-158.13.2.16 — Hair & Makeup Team & Access.
                    Independent department-scope surface. Reads
                    beta_departments.hair_makeup.members[] via
                    /dept-members. H&M dept admins (or the Production
                    Owner via oversight override) can promote/demote/
                    remove H&M members. Writes NEVER touch
                    beta_projects.members[] or any other dept. */}
                {apiBase === "/beta/hm-app/projects" ? (
                    <section
                        data-testid="settings-dept-members"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <UserCog size={14} className="text-neutral-400" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                                Team &amp; access · Hair &amp; Makeup privileges
                            </h2>
                        </div>
                        <p
                            data-testid="settings-dept-members-intro"
                            className="text-xs text-neutral-400 mb-3 leading-relaxed"
                        >
                            Department-level privileges — who does what inside Hair &amp; Makeup.
                            H&amp;M Administrators (invite, manage, delete), Contributors (add and
                            edit characters, scenes, continuity) and Viewers (read-only). These
                            are independent from Production privileges above.
                        </p>
                        {deptMembers.length === 0 ? (
                            <p className="text-sm text-neutral-400">No H&amp;M members yet.</p>
                        ) : (
                            <ul
                                data-testid="settings-dept-member-list"
                                className="space-y-2"
                            >
                                {deptMembers.map((m) => {
                                    const label = m.name || m.email || m.user_id;
                                    return (
                                        <li
                                            key={m.user_id}
                                            data-testid={`settings-dept-member-${m.user_id}`}
                                            className="flex items-center justify-between gap-3 rounded-lg bg-black/20 p-3"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-white truncate">
                                                    {label}
                                                </div>
                                                <div className="text-[11px] text-neutral-500 truncate">
                                                    {m.email}
                                                </div>
                                            </div>
                                            {canManageDept ? (
                                                <>
                                                    <select
                                                        data-testid={`settings-dept-role-${m.user_id}`}
                                                        value={m.role}
                                                        disabled={deptBusy === m.user_id}
                                                        onChange={(e) => changeDeptRole(m.user_id, e.target.value)}
                                                        className="text-xs bg-neutral-900 border border-white/10 rounded px-2 py-1 text-white"
                                                    >
                                                        <option value="admin">H&amp;M Administrator</option>
                                                        <option value="contributor">H&amp;M Contributor</option>
                                                        <option value="viewer">H&amp;M Viewer</option>
                                                    </select>
                                                    <Button
                                                        data-testid={`settings-dept-remove-${m.user_id}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        disabled={deptBusy === m.user_id}
                                                        onClick={() => removeDeptMember(m.user_id, label)}
                                                        className="text-neutral-400 hover:text-red-400"
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </>
                                            ) : (
                                                <span
                                                    data-testid={`settings-dept-role-readonly-${m.user_id}`}
                                                    className="text-[11px] uppercase tracking-[0.2em] text-neutral-400"
                                                >
                                                    {m.role === "admin"
                                                        ? "H&M Administrator"
                                                        : m.role === "viewer"
                                                          ? "H&M Viewer"
                                                          : "H&M Contributor"}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                ) : null}

                {/* Iter-158.13.2.20e.5 — Script Access rehomed to Team &
                    Access. Canonical Script is Production-owned, so
                    authority over who may READ it belongs alongside
                    the other Production Team & Access controls. This
                    section is intentionally suppressed on the H&M
                    Settings surface (`/beta/hm-app/projects/:pid/settings`)
                    via `canManageProductionTA` — a Department role
                    (H&M Administrator, Contributor, Viewer) must never
                    confer authority over the screenplay. Reuses the
                    same `commitScriptVisibility` + `script_visibility`
                    state and enforcement — no second permission system.
                    Distinct from Manage Script (per-member permission
                    that governs upload/replace/revise). */}
                {canManageProductionTA && !isArchived ? (
                    <section
                        data-testid="settings-script-access"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-sky-400/40"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldCheck size={14} className="text-sky-300" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                                Script access
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-2 leading-relaxed">
                            Some productions restrict the original screenplay
                            to Administrators while the wider team continues
                            to work with scenes, characters, schedules and
                            continuity records.
                        </p>
                        <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">
                            Changing this does not delete the script. You can
                            switch back to Production team at any time. This
                            controls who may READ the script — separate from
                            Manage Script (per-member permission below), which
                            controls who may upload, replace or revise it.
                        </p>
                        <div className="mt-4">
                            <label className="text-xs text-neutral-300 block">
                                <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                    Who can read the script?
                                </span>
                                <select
                                    data-testid="settings-script-access-select"
                                    value={project?.script_visibility || "everyone"}
                                    onChange={(e) =>
                                        commitScriptVisibility(e.target.value)
                                    }
                                    disabled={scriptVisBusy}
                                    className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-foreground disabled:opacity-50"
                                >
                                    <option value="everyone">Production team</option>
                                    <option value="owner_admin">Administrators only</option>
                                </select>
                                <p className="text-[11px] text-neutral-400 mt-1.5 leading-relaxed">
                                    {project?.script_visibility === "owner_admin"
                                        ? "Only Owner + Administrators can open the script PDF, see scene descriptions, character descriptions, and revision diffs. Scenes, characters, continuity records and schedules remain visible to everyone."
                                        : "Every production member can read the script and all parser-derived text. This is the default for most productions."}
                                </p>
                                <p className="text-[10px] text-neutral-500 mt-1 leading-relaxed">
                                    Administrators can always see the script
                                    so they can govern continuity. Choose
                                    &ldquo;Administrators only&rdquo; for
                                    productions where the script is under NDA
                                    or otherwise sensitive. Department roles
                                    (H&amp;M Administrator, Contributor,
                                    Viewer) never confer script access on
                                    their own.
                                </p>
                            </label>
                        </div>
                    </section>
                ) : null}

                {/* Iter-157F — Group 4: Recovery & Protection. Owner /
                    admin only — non-managers see the page jump
                    straight from Team to Lifecycle. */}
                {/* Iter-157N — Project Protection trust card moved to
                    the TOP of Settings (search for
                    `settings-protection-header` above the
                    `settings-error` paragraph). It introduces
                    Settings as a safe place before any operational
                    mechanics. The Storage / Restore Points /
                    Export sections remain in their canonical
                    positions below — they're the mechanics the
                    trust banner above prepares the user for. */}

                {/* Iter-157M — Storage section. Answers "Where is
                    my stuff?". Simple language, no metadata talk,
                    no implementation details. Points to the
                    Backups page where the user can connect /
                    manage their storage provider (Dropbox today,
                    more later). */}
                {/* Iter-158.13.2.20e.4 — Storage / Backup is now
                    dual-mounted at Production scope. Section renders
                    on legacy + Production mounts.
                    Iter-158.13.2.20e.7 — hidden on H&M dept mount;
                    Storage/Backup is Production-owned authority per
                    the .20e.4 rehome, and the H&M Administrator role
                    must not present as owning that surface. */}
                {canManageProductionOnly ? (
                    <section
                        data-testid="settings-storage"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-emerald-400/30"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Database size={14} className="text-emerald-300" />
                            <h2
                                data-testid="settings-storage-title"
                                className="text-xs uppercase tracking-[0.25em] text-neutral-400"
                            >
                                Storage
                            </h2>
                        </div>
                        <p
                            data-testid="settings-storage-where"
                            className="text-sm text-neutral-200 mt-3 leading-relaxed"
                        >
                            Your scripts, photos, documents and schedules are
                            stored in your connected storage provider — kept
                            with the project and yours to access at any time.
                        </p>
                        <p className="text-[12px] text-neutral-400 mt-2 leading-relaxed">
                            Connect Dropbox to mirror every file you upload to
                            your own folder. Open the storage page below to
                            view connection status, browse your folder and
                            switch providers.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Button
                                type="button"
                                data-testid="settings-open-storage"
                                onClick={() => navigate(`${routeBase}/${projectId}/backups`)}
                                className="min-tap font-semibold"
                            >
                                Storage
                            </Button>
                        </div>
                    </section>
                ) : null}

                {/* Iter-157M — Restore Points section.
                    Previously "Backups, history & restore" with
                    technical language about metadata snapshots
                    and recovery cadence. Now a single calm
                    paragraph that answers "can I recover
                    mistakes?". The Attic terminology has been
                    fully retired — the button reads "Restore
                    Points" (Iter-157O.1 destination naming
                    cleanup) and the URL/testid kept the legacy
                    `attic` slug for compatibility with existing
                    bookmarks and automation.
                    Iter-158.13.2.20e.7 — hidden on H&M dept mount
                    per the Production/Department UI separation. */}
                {canManageProductionOnly ? (
                    <section
                        data-testid="settings-backups"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-sky-400/30"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Archive size={14} className="text-sky-300" />
                            <h2
                                data-testid="settings-restore-title"
                                className="text-xs uppercase tracking-[0.25em] text-neutral-400"
                            >
                                Restore Points
                            </h2>
                        </div>
                        <p
                            data-testid="settings-restore-blurb"
                            className="text-sm text-neutral-200 mt-3 leading-relaxed"
                        >
                            Create restore points before major changes, then
                            return to earlier versions of your project if
                            mistakes are made.
                        </p>
                        <p className="text-[12px] text-neutral-500 mt-2 leading-relaxed">
                            Restoring never erases history — a safety restore
                            point of the current state is always saved first.
                            Owner &amp; admin only.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Button
                                type="button"
                                data-testid="settings-open-attic"
                                onClick={() => navigate(`${routeBase}/${projectId}/attic`)}
                                className="min-tap font-semibold"
                            >
                                Restore Points
                            </Button>
                        </div>
                        {/* Iter-157B Phase 4 — Archive integrity card. Only
                            rendered when the project is archived (there's
                            no manifest to talk about otherwise). */}
                        {isArchived ? (
                            <div className="mt-4" data-testid="settings-archive-integrity-wrapper">
                                <ArchiveIntegrityCard
                                    projectId={projectId}
                                    projectName={project?.name}
                                    role={project?.role}
                                />
                            </div>
                        ) : null}
                    </section>
                ) : null}

                {/* Iter-157F — Legal & Governance moved to the very
                    bottom of the page (after Lifecycle) so policy
                    reference no longer competes visually with
                    operational controls. The block itself lives
                    below the Danger zone.

                    Iter-157F — Leave Project moved INTO the
                    Ownership & Lifecycle group below; renders only
                    for non-owners (its own gating). */}

                {/* Iter-157 — Project & Archive Export. Owner/Admin
                    only. Composes with archive (exempted from the
                    write-lock so wrap exports always work).
                    Iter-157M — copy simplified to answer "can I
                    leave with everything?". Technical disclosures
                    (CSV cross-references, original filenames,
                    30-day pruning) live in the Export dialog
                    itself where they're contextually relevant.
                    Iter-158.13.2.20e.7 — hidden on H&M dept mount.
                    Complete Production Export is Production-owned;
                    H&M-specific per-scene/character report exports
                    live on their own department pages. */}
                {canManageProductionOnly ? (
                    <section
                        data-testid="settings-export"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-violet-500/40"
                    >
                        <div className="flex items-center gap-2">
                            <Package size={14} className="text-violet-300" />
                            <h2
                                data-testid="settings-export-title"
                                className="text-xs uppercase tracking-[0.25em] text-neutral-400"
                            >
                                Export
                            </h2>
                        </div>
                        <p
                            data-testid="settings-export-blurb"
                            className="text-sm text-neutral-200 mt-3 leading-relaxed"
                        >
                            Create a complete project package including
                            media, notes, reports and project data. Exports
                            can be stored independently of OnSetFlow — take
                            everything with you.
                        </p>
                        <Button
                            data-testid="settings-export-open"
                            variant="secondary"
                            onClick={() => setExportOpen(true)}
                            className="mt-4 min-tap"
                        >
                            <Package size={14} className="mr-2" />
                            {project?.archived ? "Create wrap export" : "Create export"}
                        </Button>

                        <RecentExportsList
                            projectId={projectId}
                            refreshKey={exportRefreshKey}
                            onChange={() =>
                                setExportRefreshKey((v) => v + 1)
                            }
                        />
                    </section>
                ) : null}

                {/* Iter-157F — Group 5: Ownership & Lifecycle. Holds
                    every action that changes WHO the project belongs
                    to or whether the project still lives. The order
                    is deliberate per the IA spec — Transfer before
                    Archive, Archive before Delete — so the user
                    walks down increasing levels of consequence. The
                    group header renders for every role; the cards
                    inside gate themselves (Transfer / Archive /
                    Delete = owner only; Leave = non-owner only).
                    Iter-158.13.2.20e.7 — Production lifecycle is
                    Production-owned. Header + all Owner-only actions
                    (Transfer, Archive, Delete) suppressed on H&M
                    dept scope. Leave Project remains available on
                    every scope so a member is never trapped. */}
                {!isHmDeptScope ? (
                    <SectionGroupHeader
                        index={5}
                        title="Governance"
                        question="Who does this production belong to, and what's its lifecycle?"
                        testid="settings-group-lifecycle"
                    />
                ) : null}

                {/* Iter-157C — Transfer Ownership. Owner-only,
                    irreversible. Lives at the TOP of Lifecycle
                    because it's the first lever an owner reaches
                    for when handing the project over.
                    Iter-158.13.2.20e.7 — Production-scope only. */}
                {canOwnProductionOnly ? (
                    <section
                        data-testid="settings-governance"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-amber-500/40"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldCheck size={14} className="text-amber-400" />
                            <h2
                                data-testid="settings-governance-heading"
                                className="text-xs uppercase tracking-[0.25em] text-neutral-400"
                            >
                                Ownership &amp; lifecycle
                            </h2>
                        </div>
                        <p className="text-xs text-neutral-400 mt-2 leading-relaxed max-w-2xl">
                            Irreversible owner-level actions live here so
                            they&rsquo;re never mixed up with day-to-day team
                            management. Transfer first, archive second,
                            delete last &mdash; each step has more
                            consequence than the one before.
                        </p>

                        <div
                            data-testid="settings-transfer-ownership-card"
                            className="mt-4 rounded-lg border border-amber-300/60 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/5 p-3"
                        >
                            <div className="flex items-start gap-3">
                                <ShieldCheck
                                    size={16}
                                    className="text-amber-700 dark:text-amber-200 mt-0.5 shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[12px] uppercase tracking-[0.2em] text-amber-800 dark:text-amber-200 font-semibold">
                                        Transfer Ownership
                                    </p>
                                    <p className="text-[13px] text-amber-900 dark:text-amber-50 mt-1 leading-relaxed">
                                        Hand the project over permanently.
                                        The new Owner gains full governance
                                        control; you become an Administrator.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    data-testid="settings-transfer-ownership-open"
                                    onClick={() => setTransferOpen(true)}
                                    disabled={members.filter((m) => m.role !== "owner").length === 0}
                                    className="tap inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                                    title={
                                        members.filter((m) => m.role !== "owner").length === 0
                                            ? "Invite someone first to transfer ownership"
                                            : "Open the Transfer Ownership dialog"
                                    }
                                >
                                    Transfer&hellip;
                                </button>
                            </div>
                        </div>
                    </section>
                ) : null}

                {/* Iter-158.13.2.20f.26b — Leave Project card RESTORED.
                    .20f.26 removed this section on the (incorrect)
                    assumption that MyProjects and BetaProject already
                    hosted a canonical Team & Access leave route. Human
                    UAT on iPhone Safari (Tree Frog) rejected that
                    assumption: the correct information architecture
                    places Leaving a Production INSIDE Production
                    Management / Governance (this surface), and the
                    duplicate entry points on the Production landing
                    (subordinate Production Management card +
                    standalone Leave card) are removed instead. The
                    card, its .20f.26-preserved state hooks
                    (`leaveOpen`/`leaveBusy`), `commitLeave`, and the
                    `confirm-leave-project` ConfirmDialog wiring are
                    reinstated verbatim from the prior Iter-156A.5 /
                    Iter-157F implementation — no business-logic
                    change, no permission change, no endpoint change,
                    no copy change. */}
                {project && !isOwner ? (
                    <section
                        data-testid="settings-leave-project"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-neutral-500/40"
                    >
                        <div className="flex items-center gap-2">
                            <LogOut size={14} className="text-neutral-300" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                                Leave project
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-3 leading-relaxed">
                            You can step away from this project at any time. Your
                            access is revoked immediately and the project will
                            disappear from your list.
                        </p>
                        <p className="text-[12px] text-neutral-400 mt-2 leading-relaxed">
                            Your contributions (photos, notes, looks) stay on
                            the project &mdash; attribution is preserved.
                        </p>
                        <Button
                            data-testid="settings-leave-project-open"
                            variant="secondary"
                            onClick={() => setLeaveOpen(true)}
                            className="mt-4 min-tap"
                        >
                            <LogOut size={14} className="mr-2" />
                            Leave project
                        </Button>
                    </section>
                ) : null}

                {/* Iter-135 — Archive lifecycle. Owner-only,
                    non-destructive. Sits ABOVE the Danger Zone so the
                    softer-than-delete action is the first thing an
                    owner sees when wrapping a production.
                    Iter-158.13.2.20e.7 — Production-scope only. */}
                {canOwnProductionOnly ? (
                    <section
                        data-testid="settings-archive"
                        className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-neutral-500/40"
                    >
                        <div className="flex items-center gap-2">
                            <Archive size={14} className="text-neutral-300" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                                Archive project
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-3 leading-relaxed">
                            Archived projects are removed from the active
                            workspace but remain recoverable later. Every
                            scene, character, capture, note, flag, schedule,
                            look and audit entry is preserved exactly as it
                            is now.
                        </p>
                        <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                            Wrapped a production? Archive instead of delete —
                            future pickup, reshoot or reference access stays
                            one tap away.
                        </p>
                        <Button
                            data-testid="settings-archive-open"
                            variant="secondary"
                            onClick={() => setArchiveConfirmOpen(true)}
                            className="mt-4 min-tap"
                        >
                            <Archive size={14} className="mr-2" />
                            Archive project
                        </Button>
                    </section>
                ) : null}

                {/* Iter-158.13.2.20f.28 — Delete Project relocated to
                    the ABSOLUTE BOTTOM of Production Management,
                    immediately below Legal & Governance. Previously
                    lived here in Group 5 (Governance / Ownership &
                    Lifecycle) between Archive and Legal & Governance,
                    where the destructive action was visually buried
                    beneath calmer legal content. Every existing
                    safeguard is preserved unchanged:
                      · gate: `canOwnProductionOnly` (Production
                        Owner on Production scope only)
                      · destructive-styled section with red border-l
                      · confirmation dialog `delete-project-dialog`
                        remains wired to `settings-danger` via
                        `setDeleteOpen(true)`
                      · backend delete endpoint UNCHANGED
                      · testid `settings-danger` and button testid
                        `delete-project-open` preserved verbatim
                    See the relocated section immediately before the
                    closing `</main>` tag below. */}

                {/* Iter-157F — Group 6: Legal & Governance. Reference
                    material only. Lives at the very bottom so it
                    never competes visually with operational
                    controls. Visible to everyone (transparency over
                    hiding). Links / payloads dormant for now and
                    will fill in as policy artefacts ship. */}
                <SectionGroupHeader
                    index={6}
                    title="Legal & governance"
                    question="Where are the policies and agreements?"
                    testid="settings-group-legal"
                />
                <section
                    data-testid="settings-legal"
                    className="rounded-xl border border-white/10 bg-card p-5 mb-5 border-l-2 border-l-neutral-500/30"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck size={14} className="text-neutral-400" />
                        <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                            Legal &amp; governance
                        </h2>
                    </div>
                    <p className="text-sm text-neutral-300 mt-2 leading-relaxed">
                        OnSetFlow is a shared production memory tool. These
                        anchors will surface the policy your production has
                        accepted, so the people protecting continuity also
                        know what they&apos;ve signed.
                    </p>
                    <ul
                        data-testid="settings-legal-list"
                        className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
                    >
                        {[
                            { k: "terms", label: "Terms & conditions" },
                            { k: "privacy", label: "Privacy policy" },
                            { k: "media-ownership", label: "Media ownership" },
                            { k: "data-retention", label: "Data retention" },
                            { k: "nda", label: "NDA expectations" },
                            { k: "export-rights", label: "Export rights" },
                        ].map((row) => (
                            <li
                                key={row.k}
                                data-testid={`settings-legal-${row.k}`}
                                className="flex items-center justify-between gap-2 rounded-md border border-white/5 bg-secondary/30 px-3 py-2"
                            >
                                <span className="text-xs text-neutral-200">
                                    {row.label}
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                    Coming soon
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Iter-133 — Danger zone is OWNER-ONLY. Even
                    administrators must defer the single irreversible
                    governance action to the project owner, so production
                    memory can't be erased by a delegate.
                    Iter-158.13.2.20e.7 — Production-scope only.
                    Iter-158.13.2.20f.28 — Moved here (absolute bottom
                    of Production Management, after Legal & Governance)
                    so the destructive action is the last thing on the
                    page — spatially and visually separated from calmer
                    lifecycle content. All existing gates, copy, and
                    the wired ConfirmDialog stay verbatim; only the
                    JSX position changed. Extra top margin gives the
                    section visible breathing room from the Legal
                    block above. */}
                {canOwnProductionOnly ? (
                    <section
                        data-testid="settings-danger"
                        className="rounded-xl border border-red-500/20 bg-red-500/5 p-5 mt-10 border-l-2 border-l-red-500/40"
                    >
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={14} className="text-red-300" />
                            <h2 className="text-xs uppercase tracking-[0.25em] text-red-200">
                                Danger zone
                            </h2>
                        </div>
                        <p className="text-sm text-neutral-300 mt-3 leading-relaxed">
                            Deleting this project removes all scenes, cast,
                            script, captures, notes, flags, looks and audit
                            history — for every collaborator. This cannot be
                            undone, and only the project owner can perform it.
                        </p>
                        <Button
                            data-testid="delete-project-open"
                            variant="destructive"
                            onClick={() => setDeleteOpen(true)}
                            className="mt-4 min-tap"
                        >
                            <Trash2 size={14} className="mr-2" />
                            Delete project
                        </Button>
                    </section>
                ) : null}
            </main>

            <Dialog open={deleteOpen} onOpenChange={(v) => !deleting && setDeleteOpen(v)}>
                <DialogContent
                    data-testid="delete-project-dialog"
                    className="max-w-md bg-card border-red-500/30"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-bold text-white">
                            Delete this project?
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            This will permanently remove{" "}
                            <span className="text-white font-semibold">{project?.name}</span>{" "}
                            and all of its data for every collaborator.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-4">
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Type the project name to confirm
                        </Label>
                        <Input
                            data-testid="delete-confirm-input"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={project?.name || ""}
                            className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-red-500"
                        />
                        {error ? (
                            <p data-testid="delete-error" className="text-sm text-red-400">
                                {error}
                            </p>
                        ) : null}
                        <div className="flex gap-2 pt-2">
                            <Button
                                data-testid="delete-cancel"
                                variant="secondary"
                                onClick={() => setDeleteOpen(false)}
                                disabled={deleting}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="delete-confirm"
                                variant="destructive"
                                onClick={doDelete}
                                disabled={!nameOk || deleting}
                                className="flex-1 min-tap"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2 size={14} className="mr-2 animate-spin" />
                                        Deleting…
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={14} className="mr-2" /> Delete
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={inviteOpen} onOpenChange={closeInviteDialog}>
                <DialogContent
                    data-testid="invite-member-dialog"
                    className="max-w-md bg-card border-white/10"
                >
                    <DialogHeader className="pr-8">
                        <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <UserPlus size={18} className="text-emerald-300" />
                            Invite a teammate
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            {invitePreview ? (
                                <>Copy and send the message below to{" "}
                                    <span className="text-white">
                                        {invitePreview.recipient_name}
                                    </span>
                                    .</>
                            ) : (
                                <>They&rsquo;ll get the project name, the access
                                    code, and a one-tap join link.</>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    {!invitePreview ? (
                        <form
                            data-testid="invite-form"
                            onSubmit={submitInvite}
                            className="space-y-3 mt-4"
                        >
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="invite-name"
                                    className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                >
                                    Name
                                </Label>
                                <Input
                                    id="invite-name"
                                    data-testid="invite-name-input"
                                    autoFocus
                                    value={inviteName}
                                    onChange={(e) => setInviteName(e.target.value)}
                                    placeholder="Natasha Lee"
                                    maxLength={120}
                                    className="h-12 bg-secondary border-transparent text-base focus-visible:ring-1 focus-visible:ring-emerald-400"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="invite-email"
                                    className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                                >
                                    Email
                                </Label>
                                <Input
                                    id="invite-email"
                                    data-testid="invite-email-input"
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="natasha@example.com"
                                    className="h-12 bg-secondary border-transparent text-base focus-visible:ring-1 focus-visible:ring-emerald-400"
                                />
                            </div>
                            {inviteError ? (
                                <p
                                    data-testid="invite-error"
                                    className="text-sm text-red-400"
                                >
                                    {inviteError}
                                </p>
                            ) : null}
                            <div className="flex gap-2 pt-2">
                                <Button
                                    type="button"
                                    data-testid="invite-cancel"
                                    variant="secondary"
                                    onClick={() => closeInviteDialog(false)}
                                    disabled={inviteSubmitting}
                                    className="flex-1 min-tap"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    data-testid="invite-submit"
                                    disabled={
                                        inviteSubmitting ||
                                        !inviteName.trim() ||
                                        !inviteEmail.trim()
                                    }
                                    className="flex-1 min-tap bg-emerald-500 hover:bg-emerald-500/90 text-neutral-900 font-semibold"
                                >
                                    {inviteSubmitting ? (
                                        <>
                                            <Loader2
                                                size={14}
                                                className="mr-2 animate-spin"
                                            />
                                            Generating…
                                        </>
                                    ) : (
                                        <>
                                            <Mail size={14} className="mr-2" />
                                            Generate invite
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-3 mt-4">
                            <div className="rounded-lg border border-emerald-400/20 bg-emerald-400/5 p-3">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-200">
                                    To
                                </div>
                                <div
                                    data-testid="invite-preview-recipient"
                                    className="text-white font-medium mt-0.5"
                                >
                                    {invitePreview.recipient_name}{" "}
                                    <span className="text-neutral-400 font-normal">
                                        &lt;{invitePreview.recipient_email}&gt;
                                    </span>
                                </div>
                            </div>
                            <div className="rounded-lg border border-white/10 bg-secondary/40 p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                        Access code
                                    </div>
                                    <span
                                        data-testid="invite-preview-code"
                                        className="mono text-emerald-300 tracking-[0.3em] text-sm"
                                    >
                                        {invitePreview.invite_code}
                                    </span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                    Invitation message
                                </div>
                                <textarea
                                    data-testid="invite-preview-message"
                                    readOnly
                                    value={invitePreview.message}
                                    onFocus={(e) => e.target.select()}
                                    rows={10}
                                    className="w-full bg-neutral-900/60 border border-white/10 rounded-md text-sm text-neutral-100 p-3 font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-emerald-400 resize-none"
                                />
                            </div>
                            <p className="text-xs text-neutral-500 leading-relaxed">
                                Real email isn&rsquo;t wired yet — paste this into
                                your messaging app of choice.
                            </p>
                            <div className="flex gap-2 pt-1">
                                <Button
                                    type="button"
                                    data-testid="invite-preview-close"
                                    variant="secondary"
                                    onClick={() => closeInviteDialog(false)}
                                    className="flex-1 min-tap"
                                >
                                    Done
                                </Button>
                                <Button
                                    type="button"
                                    data-testid="invite-preview-copy"
                                    onClick={copyInvite}
                                    className="flex-1 min-tap bg-emerald-500 hover:bg-emerald-500/90 text-neutral-900 font-semibold"
                                >
                                    {inviteCopied ? (
                                        <>
                                            <Check size={14} className="mr-2" />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={14} className="mr-2" />
                                            Copy invitation
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Iter-116 — QR dialog for in-room handoff. Renders the
                invite URL as a scannable code so a teammate can join
                from their phone without typing the code.
                Iter-158.13.2.7 — QR image AND caption converge on the
                same invitation object as the settings pill + Copy
                Link. In dept context we pass shareable.code; on
                non-dept surfaces the legacy project code is passed
                unchanged. Eliminates the "two codes in one modal"
                confusion.
                Iter-158.13.2.20f.1 (#6) — pass projectId + apiBase
                context so the dialog's existing Regenerate button
                actually renders for Production Owner. Backend
                endpoint /invite-code/regenerate is already mounted
                on both the Production T&A factory (.20e.2) and the
                H&M factory. Without these props the dialog silently
                dropped the CTA on Production scope. */}
            <ProjectInviteQRDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                projectId={projectId}
                inviteCode={
                    inviteDepartment
                        ? (shareable?.code || "")
                        : project?.invite_code
                }
                inviteLink={inviteLink}
                projectName={project?.name}
                canRegenerate={
                    inviteDepartment
                        ? (canManage === true)
                        : (isOwner === true)
                }
                onRegenerated={(data) => {
                    if (inviteDepartment) {
                        // Department shareable rotation. Patch
                        // local shareable state so the QR image +
                        // pill reflect the new code instantly.
                        if (data?.code) {
                            setShareable((prev) => prev
                                ? { ...prev, code: data.code }
                                : prev);
                        }
                    } else if (data?.invite_code) {
                        // Production invite rotation — refresh
                        // project so the new code shows immediately
                        // on the invite pill + Copy Link + QR.
                        setProject((prev) => prev
                            ? { ...prev, invite_code: data.invite_code }
                            : prev);
                    }
                }}
            />

            {/* Iter-157 — Export creation dialog */}
            <ExportDialog
                open={exportOpen}
                onOpenChange={setExportOpen}
                project={project}
                onCreated={() => setExportRefreshKey((v) => v + 1)}
            />

            {/* Iter-135 — Archive confirmation modal. Calm, owner-only,
                non-destructive. Separate from the Danger Zone Delete
                modal so the visual hierarchy matches the lifecycle:
                Active → Archived → (optional later Delete). */}
            <Dialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
                <DialogContent
                    data-testid="archive-project-confirm-dialog"
                    className="bg-card border-white/10"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                            <Archive size={18} className="text-neutral-300" />
                            Archive this project?
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400 leading-relaxed">
                            The project will be removed from the active
                            workspace but can be restored later by the
                            project owner. All scenes, cast, captures,
                            notes, flags, looks and audit history are
                            preserved.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-2 pt-2">
                        <Button
                            type="button"
                            data-testid="archive-project-cancel"
                            variant="secondary"
                            onClick={() => setArchiveConfirmOpen(false)}
                            disabled={archiving}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="archive-project-confirm"
                            onClick={async () => {
                                setArchiving(true);
                                try {
                                    // Iter-158.13.2.17d — Archive is
                                    // Production-scope lifecycle. Route
                                    // 2 mounts it at
                                    // /api/beta/projects/{pid}/archive,
                                    // NOT under the workspace prefix.
                                    // Helper redirects appropriately so
                                    // callers inside /beta/hm-app/… stop
                                    // 404'ing.
                                    await api.post(
                                        lifecycleEndpoint(apiBase, projectId, "archive"),
                                    );
                                    toast.success(
                                        "Project archived. Find it under Archived in My Projects.",
                                    );
                                    setArchiveConfirmOpen(false);
                                    navigate("/");
                                } catch (e) {
                                    toast.error(formatApiError(e));
                                } finally {
                                    setArchiving(false);
                                }
                            }}
                            disabled={archiving}
                            className="flex-1 min-tap"
                        >
                            {archiving ? (
                                <>
                                    <Loader2 size={14} className="mr-2 animate-spin" />
                                    Archiving…
                                </>
                            ) : (
                                <>
                                    <Archive size={14} className="mr-2" />
                                    Archive project
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Iter-156A.4 — Role-change confirmation dialog. Generic
                ConfirmDialog with role-specific copy. */}
            <ConfirmDialog
                open={Boolean(pendingRoleChange)}
                onOpenChange={(v) => { if (!v) setPendingRoleChange(null); }}
                title={
                    pendingRoleChange
                        ? `Change ${
                              pendingRoleChange.member.name ||
                              pendingRoleChange.member.email
                          } to ${ROLE_LABELS[pendingRoleChange.nextRole]}?`
                        : ""
                }
                description={
                    pendingRoleChange
                        ? `${ROLE_LABELS[pendingRoleChange.nextRole]}: ${
                              ROLE_DESCRIPTIONS[pendingRoleChange.nextRole]
                          }\n\nYou can change this again any time.`
                        : ""
                }
                confirmLabel={
                    pendingRoleChange
                        ? `Set as ${ROLE_LABELS[pendingRoleChange.nextRole]}`
                        : "Confirm"
                }
                cancelLabel="Cancel"
                tone="primary"
                onConfirm={commitRoleChange}
                testidPrefix="confirm-role-change"
            />

            {/* Iter-156A.4 — Remove member confirmation dialog. */}
            <ConfirmDialog
                open={Boolean(pendingRemove)}
                onOpenChange={(v) => { if (!v) setPendingRemove(null); }}
                title={
                    pendingRemove
                        ? `Remove ${pendingRemove.name || pendingRemove.email} from the project?`
                        : ""
                }
                description={
                    "Their access will be revoked immediately. The project will disappear from their list and any further actions will be refused.\n\nTheir contributions (photos, notes, looks) stay on the project — attribution is preserved."
                }
                confirmLabel="Remove from project"
                cancelLabel="Cancel"
                tone="danger"
                onConfirm={commitRemoveMember}
                testidPrefix="confirm-remove-member"
            />

            {/* Iter-156A.5 Phase 5 — Leave Project confirmation dialog.
                Confirm-then-redirect-to-/projects. */}
            <ConfirmDialog
                open={leaveOpen}
                onOpenChange={(v) => { if (!v && !leaveBusy) setLeaveOpen(false); }}
                title={`Leave ${project?.name || "this project"}?`}
                description={
                    "Your access will be revoked immediately and the project will disappear from your list.\n\nYour contributions (photos, notes, looks) stay on the project — attribution is preserved. You can be re-invited later."
                }
                confirmLabel={leaveBusy ? "Leaving…" : "Leave project"}
                cancelLabel="Cancel"
                tone="danger"
                onConfirm={commitLeave}
                testidPrefix="confirm-leave-project"
            />

            {/* Iter-156A.4 — Transfer ownership dialog. */}
            <TransferOwnershipDialog
                open={transferOpen}
                onOpenChange={setTransferOpen}
                members={members}
                onConfirm={commitTransfer}
                busy={transferBusy}
            />
        </div>
    );
}
